import { createTheme } from "@mui/material";
import SegoeBold from "../../assets/fonts/SegoeBold.ttf";
import SegoeRegular from "../../assets/fonts/SegoeRegular.ttf";
import SegoeSemibold from "../../assets/fonts/SegoeSemibold.ttf";

const purple = {
  950: "#1a2166",
  900: "#2b3598",
  800: "#3945b7",
  700: "#4754d6",
  600: "#5563f5",
  500: "#8da1fa",
  400: "#abbcff",
  300: "#c0cfff",
  200: "#d2ddff",
  100: "#e1e9ff",
  50: "#edf2ff",
  25: "#f7f9ff",
  15: "#fdfdff",
};

const neutral = {
  950: "#262626",
  900: "#404040",
  800: "#595959",
  700: "#737373",
  600: "#8c8c8c",
  500: "#bbbbbb",
  400: "#cecece",
  300: "#d9d9d9",
  200: "#e1e1e1",
  100: "#e8e8e8",
  25: "#f6f6f5",
  15: "#fcfcfd",
  0: "#ffffff",
  transparent: "#ffffff00",
  overlay: "#26262666",
};

const yellow = {
  950: "#594705",
  900: "#8c7008",
  800: "#b3900a",
  700: "#d9af0d",
  600: "#ffce0f",
  500: "#ffd61f",
  400: "#ffdd33",
  300: "#ffe766",
  200: "#fff099",
  100: "#fff8cc",
  50: "#fffdf0",
  25: "#fffae9",
  15: "#fefdfb",
};

const orange = {
  950: "#4d2506",
  900: "#7a3b0a",
  800: "#9e4d0e",
  700: "#c25e11",
  600: "#e66f14",
  500: "#ff852e",
  400: "#ff8f47",
  300: "#ffa06b",
  200: "#ffc5a3",
  100: "#ffe2d1",
  50: "#fff4ed",
  25: "#fff5ed",
  15: "#fffcfa",
};

const red = {
  950: "#541217",
  900: "#8a1f27",
  800: "#b02831",
  700: "#d13a42",
  600: "#ef4c54",
  500: "#ed8e8b",
  400: "#f6a9a6",
  300: "#febdba",
  200: "#ffcdca",
  100: "#ffdbd9",
  50: "#ffebea",
  25: "#fff7f7",
  15: "#fffcfc",
};

const green = {
  950: "#082c14",
  900: "#0d4720",
  800: "#115a28",
  700: "#156d30",
  600: "#198038",
  500: "#5ebb70",
  400: "#8fd098",
  300: "#aedfb4",
  200: "#c6e9ca",
  100: "#d8f1db",
  50: "#e7f7e9",
  25: "#fbfefb",
  15: "#f5fbf5",
};

const teal = {
  950: "#002a22",
  900: "#004538",
  800: "#005846",
  700: "#006a54",
  600: "#007b62",
  500: "#52bb9d",
  400: "#81cfb7",
  300: "#9fe0ca",
  200: "#b5ecd9",
  100: "#caf4e5",
  50: "#def9ef",
  25: "#f3fbf8",
  15: "#f9fefc",
};

const violet = {
  950: "#220055",
  900: "#311679",
  800: "#4700ae",
  700: "#5700d4",
  600: "#6600fa",
  500: "#a094ff",
  400: "#b9b4ff",
  300: "#cdcaff",
  200: "#dbd9ff",
  100: "#e6e5ff",
  50: "#f0f0ff",
  25: "#f8f8ff",
  15: "#fdfdff",
};

const magenta = {
  950: "#3d003a",
  900: "#62005e",
  800: "#7d0078",
  700: "#980092",
  600: "#b400ad",
  500: "#e287d9",
  400: "#eca5e4",
  300: "#f4bbed",
  200: "#faccf4",
  100: "#fedaf9",
  50: "#ffe8fc",
  25: "#fff7fd",
  15: "#fffcff",
};

const amber = {
  950: "#451d00",
  900: "#6f2f00",
  800: "#8d3c00",
  700: "#ab4900",
  600: "#c95600",
  500: "#e9946b",
  400: "#f5ab88",
  300: "#ffbd9a",
  200: "#ffcbaf",
  100: "#ffdac5",
  50: "#ffebdf",
  25: "#fff5f0",
  15: "#fefcfb",
};

/**
 * Cyan — added for the `Merged` status badge (2026-08-19).
 *
 * A distinct hue from `teal`, which is green-teal (#007961) and reads as a
 * success colour. Only the two steps design has supplied are defined; every
 * step on `PrimitiveColor` is optional, so the ramp fills in as more land.
 */
const cyan = {
  600: "#57E4F8",
  50: "#E1FAFC",
};

/**
 * Gold — added for the `Re-Open` status badge (2026-08-19).
 *
 * Not folded into `yellow`: every slot on that ramp is taken (700 #d9af0d,
 * 600 #ffce0f, 500 #ffd61f) and overwriting one would silently restyle its
 * existing consumers.
 */
const gold = {
  600: "#E3B309",
  50: "#F8F1CF",
};

/**
 * Raw primitive ramps, for consumers that render OUTSIDE the MUI tree.
 *
 * `@react-pdf/renderer` documents are rendered by `ReactPDF.render()`, with no
 * `ThemeProvider` above them, so `useTheme()` returns the default MUI theme
 * rather than ours — which is why `PdfStatusBadge` writes literal hex. These
 * exports let it use the same values the on-screen badge does instead of a copy
 * kept in step by comment.
 *
 * **NOT a second route to the theme in normal UI code.** Components inside the
 * app must still use `useTheme()` (CLAUDE.md); reaching for this in a screen
 * bypasses any future theme switch.
 */
export const primitiveRamps = { amber, cyan, gold, red };

const blue = {
  950: "#062d4a",
  900: "#094a79",
  800: "#0c5d97",
  700: "#0f70b5",
  600: "#1283d3",
  500: "#6fafe9",
  400: "#98c6f1",
  300: "#b3d6f9",
  200: "#c7e3fe",
  100: "#d9edff",
  50: "#e8f3fe",
  25: "#f5f9fe",
  15: "#fbfdff",
};

const indigo = {
  950: "#130440",
  900: "#1f0666",
  800: "#290885",
  700: "#3209a3",
  600: "#3c0bc1",
  500: "#949bff",
  400: "#b0b9ff",
  300: "#c6cdff",
  200: "#d6dcff",
  100: "#e3e7ff",
  50: "#eaedff",
  25: "#f7f8ff",
  15: "#fdfdff",
};

const theme = createTheme({
  zIndex: {
    modal: 999,
    drawer: 900,
    appBar: 800,
    fab: 2000,
  },

  breakpoints: {
    values: {
      xs: 0,
      sm: 1024,
      md: 1440,
      lg: 1920,
      xl: 2560,
    },
  },
  palette: {
    primary: {
      main: "#5563F5",
      hover: "#4754d6",
      pressed: "#3945b7",
      tint: "#F1F2FE",
    },
    purple: {
      main: "#5563F5",
      hover: "#4754d6",
      pressed: "#3945b7",
      tint: "#F1F2FE",
    },
    darkBg: {
      main: "#FFFFFF",
      high: "#FFFFFF",
      medium: "#E0E0E0",
      low: "#A5A5A6",
    },
    lightBg: {
      main: "#1C1E26",
      high: "#1C1E26",
      medium: "#383940",
      low: "#767B85",
    },
    error: {
      main: "#ef4c54",
      tint: "#ffebea",
    },
    success: {
      main: "#198038",
      tint: "#e7f7e9",
    },
    warning: {
      main: "#e66f14",
      tint: "#fff4ed",
    },
    info: {
      main: "#1283d3",
      tint: "#e8f3fe",
    },
    "warning-minor": {
      main: "#ffce0f",
      tint: "#fffdf0",
    },
    secondary: {
      main: "#EBFCFE",
      "purple-tint": "#8791F8",
      "purple-tint-2": "#B7BDFB",
      "purple-tint-3": "#E7E9FE",
    },
    neutral: {
      main: "#FFFFFF",
      "005": "#F6F6F5",
      "010": "#EDEDED",
      "015": "#E3E3E3",
      "020": "#CCCCCC",
      "B-002": "#F7F9FC",
      "B-005": "#F0F4FC",
      "B-010": "#E7EBF3",
      "B-015": "#DDE3EE",
      "B-020": "#CCD5E6",
    },
    primitive: {
      brand: {
        purple: {
          950: purple[950],
          900: purple[900],
          800: purple[800],
          700: purple[700],
          600: purple[600],
          500: purple[500],
          400: purple[400],
          300: purple[300],
          200: purple[200],
          100: purple[100],
          50: purple[50],
          25: purple[25],
          15: purple[15],
        },
      },
      neutrals: {
        neutral: {
          950: neutral[950],
          900: neutral[900],
          800: neutral[800],
          700: neutral[700],
          600: neutral[600],
          500: neutral[500],
          400: neutral[400],
          300: neutral[300],
          200: neutral[200],
          100: neutral[100],
          25: neutral[25],
          15: neutral[15],
          0: neutral[0],
          transparent: neutral.transparent,
          overlay: neutral.overlay,
        },
      },
      system: {
        yellow: {
          950: yellow[950],
          900: yellow[900],
          800: yellow[800],
          700: yellow[700],
          600: yellow[600],
          500: yellow[500],
          400: yellow[400],
          300: yellow[300],
          200: yellow[200],
          100: yellow[100],
          50: yellow[50],
          25: yellow[25],
          15: yellow[15],
        },
        orange: {
          950: orange[950],
          900: orange[900],
          800: orange[800],
          700: orange[700],
          600: orange[600],
          500: orange[500],
          400: orange[400],
          300: orange[300],
          200: orange[200],
          100: orange[100],
          50: orange[50],
          25: orange[25],
          15: orange[15],
        },
        green: {
          950: green[950],
          900: green[900],
          800: green[800],
          700: green[700],
          600: green[600],
          500: green[500],
          400: green[400],
          300: green[300],
          200: green[200],
          100: green[100],
          50: green[50],
          25: green[25],
          15: green[15],
        },
        teal: {
          950: teal[950],
          900: teal[900],
          800: teal[800],
          700: teal[700],
          600: teal[600],
          500: teal[500],
          400: teal[400],
          300: teal[300],
          200: teal[200],
          100: teal[100],
          50: teal[50],
          25: teal[25],
          15: teal[15],
        },
        red: {
          950: red[950],
          900: red[900],
          800: red[800],
          700: red[700],
          600: red[600],
          500: red[500],
          400: red[400],
          300: red[300],
          200: red[200],
          100: red[100],
          50: red[50],
          25: red[25],
          15: red[15],
        },
        violet: {
          950: violet[950],
          900: violet[900],
          800: violet[800],
          700: violet[700],
          600: violet[600],
          500: violet[500],
          400: violet[400],
          300: violet[300],
          200: violet[200],
          100: violet[100],
          50: violet[50],
          25: violet[25],
          15: violet[15],
        },
        magenta: {
          950: magenta[950],
          900: magenta[900],
          800: magenta[800],
          700: magenta[700],
          600: magenta[600],
          500: magenta[500],
          400: magenta[400],
          300: magenta[300],
          200: magenta[200],
          100: magenta[100],
          50: magenta[50],
          25: magenta[25],
          15: magenta[15],
        },
        amber: {
          950: amber[950],
          900: amber[900],
          800: amber[800],
          700: amber[700],
          600: amber[600],
          500: amber[500],
          400: amber[400],
          300: amber[300],
          200: amber[200],
          100: amber[100],
          50: amber[50],
          25: amber[25],
          15: amber[15],
        },
        blue: {
          950: blue[950],
          900: blue[900],
          800: blue[800],
          700: blue[700],
          600: blue[600],
          500: blue[500],
          400: blue[400],
          300: blue[300],
          200: blue[200],
          100: blue[100],
          50: blue[50],
          25: blue[25],
          15: blue[15],
        },
        indigo: {
          950: indigo[950],
          900: indigo[900],
          800: indigo[800],
          700: indigo[700],
          600: indigo[600],
          500: indigo[500],
          400: indigo[400],
          300: indigo[300],
          200: indigo[200],
          100: indigo[100],
          50: indigo[50],
          25: indigo[25],
          15: indigo[15],
        },
        // Partial ramps — only the steps design has supplied so far.
        cyan: {
          600: cyan[600],
          50: cyan[50],
        },
        gold: {
          600: gold[600],
          50: gold[50],
        },
      },
    },
  },
  typography: {
    fontFamily: "SegoeRegular",
    h64: {
      fontWeight: 700,
      fontSize: "64px",
      lineHeight: "64px",
      letterSpacing: "0px",
      fontFamily: "SegoeBold",
      color: "#1C1E26",
    },
    h1: {
      fontWeight: 700,
      fontSize: "40px",
      lineHeight: "48px",
      letterSpacing: "0px",
      fontFamily: "SegoeBold",
      color: "#1C1E26",
    },
    h2: {
      fontWeight: 700,
      fontSize: "32px",
      lineHeight: "40px",
      letterSpacing: "0px",
      fontFamily: "SegoeBold",
      color: "#1C1E26",
    },
    h3: {
      fontWeight: 700,
      fontSize: "28px",
      lineHeight: "32px",
      letterSpacing: "0px",
      fontFamily: "SegoeBold",
      color: "#1C1E26",
    },
    h4: {
      fontWeight: 700,
      fontSize: "24px",
      lineHeight: "28px",
      letterSpacing: "0px",
      fontFamily: "SegoeBold",
      color: "#1C1E26",
    },
    h5: {
      fontWeight: 700,
      fontSize: "20px",
      lineHeight: "24px",
      letterSpacing: "0px",
      fontFamily: "SegoeBold",
      color: "#1C1E26",
    },
    h6: {
      fontWeight: 600,
      fontSize: "16px",
      lineHeight: "22px",
      letterSpacing: "0px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },

    caption: {
      fontWeight: 700,
      fontSize: "12px",
      letterSpacing: "0.5px",
      lineHeight: "16px",
      fontFamily: "SegoeBold",
      color: "#1C1E26",
    },
    "caption-2": {
      fontWeight: 600,
      fontSize: "12px",
      letterSpacing: "0.5px",
      lineHeight: "16px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },
    "caption-3": {
      fontWeight: 600,
      fontSize: "10px",
      letterSpacing: "0.5px",
      lineHeight: "16px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },
    "caption-xs": {
      fontWeight: 600,
      fontSize: "10px",
      letterSpacing: "0.5px",
      lineHeight: "10px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },
    "body-large": {
      fontWeight: 400,
      fontSize: "16px",
      letterSpacing: "0px",
      lineHeight: "24px",
      fontFamily: "SegoeRegular",
      color: "#1C1E26",
    },
    "body-medium": {
      fontWeight: 400,
      fontSize: "14px",
      letterSpacing: "0px",
      lineHeight: "20px",
      fontFamily: "SegoeRegular",
      color: "#1C1E26",
    },
    "body-small": {
      fontWeight: 400,
      fontSize: "12px",
      letterSpacing: "0px",
      lineHeight: "16px",
      fontFamily: "SegoeRegular",
      color: "#1C1E26",
    },
    "body-xsmall": {
      fontWeight: 400,
      fontSize: "10px",
      letterSpacing: "0px",
      lineHeight: "16px",
      fontFamily: "SegoeRegular",
      color: "#1C1E26",
    },
    "button-label": {
      fontWeight: 600,
      fontSize: "12px",
      letterSpacing: "0px",
      lineHeight: "24px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },
    "button-large": {
      fontWeight: 600,
      fontSize: "16px",
      letterSpacing: "0px",
      lineHeight: "24px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },
    "button-small": {
      fontWeight: 600,
      fontSize: "14px",
      letterSpacing: "0px",
      lineHeight: "20px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: "14px",
      letterSpacing: "0px",
      lineHeight: "22px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },
    "link-small": {
      fontWeight: 500,
      fontSize: "14px",
      letterSpacing: "0px",
      lineHeight: "24px",
      fontFamily: "SegoeSemibold",
      color: "#5563F5",
      textDecoration: "underline",
    },
    "link-xs": {
      fontWeight: 600,
      fontSize: "12px",
      letterSpacing: "0px",
      lineHeight: "16px",
      fontFamily: "SegoeSemibold",
      color: "#5563F5",
      textDecoration: "underline",
    },
    "tag-label-xs": {
      fontWeight: 600,
      fontSize: "10px",
      letterSpacing: "0px",
      lineHeight: "16px",
      fontFamily: "SegoeSemibold",
      color: "#1C1E26",
    },
    subtitle2: undefined,
    body1: undefined,
    body2: undefined,
    overline: undefined,
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          "body-large": "p",
          "body-small": "p",
          "body-medium": "p",
          "caption-2": "span",
          "caption-3": "span",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'SegoeRegular';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: url(${SegoeRegular}) format('truetype');
        }
        @font-face {
          font-family: 'SegoeBold';
          font-style: normal;
          font-display: swap;
          font-weight: 700;
          src: url(${SegoeBold}) format('truetype');
        }
        @font-face {
          font-family: 'SegoeSemibold';
          font-style: normal;
          font-display: swap;
          font-weight: 600;
          src: url(${SegoeSemibold}) format('truetype');
        }
        ::-webkit-scrollbar {
          display: none;
        }
        ::-webkit-scrollbar-button {
          display: block;
        }
        &::-webkit-scrollbar-track {
          background-color: #E7EBF3;
          border: none;
          border-radius: 8px;
          padding: 0 8px;
        }
        &::-webkit-scrollbar-track-piece {
          background-color: transparent;
          border: none;
        }
        &::-webkit-scrollbar-thumb {
          background-color: #FFF;
          border-radius: 8px;
          border: 2px solid #CCD5E6;
          margin: 4px;
        }
        * {
          scrollbar-width: none;
          scrollbar-color: #CCC #FFF;
        }
      `,
    },
  },
});

export default theme;
